import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Student = {
  _id: string
  grNumber: string
  email: string
  fatherCnic: string
  motherEmail: string
  motherCnic: string
  fatherPhoneNumber: string
  motherPhoneNumber: string
  guardianPhoneNumber: string
  guardianCnic: string
  user: User
  appliedClass: {
    _id: string
    name: string
  }
  section: {
    _id: string
    name: string
  }
  admission: {
    _id: string
    firstName: string
    lastName: string
    dateOfBirth: string
    gender: string
    fatherName: string
    status: string
    branch: {
      _id: string
      name: string
    }
    session: {
      _id: string
      title: string
    }
    system: {
      _id: string
      name: string
    }
    fatherPhoneNumber: string
    fatherEmail: string
    studentPicture: string
  }
  createdAt: string
  updatedAt: string
  isDeleted: boolean
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type ApiResponse = {
  data: Student[]
  pagination: PaginationData
}

export type StudentFilters = {
  page?: number
  limit?: number
  searchText?: string
  class?: string
  branch?: string
  session?: string
  system?: string
  gender?: string
  status?: string
  sortBy?: string
  section?: string
  feeGroup?: string
  _t?: number
}

// ✅ NEW TYPES FOR LOGIN DETAILS UPDATE
export type UpdateLoginDetailsRequest = {
  email?: string
  password?: string
  isActive?: boolean
}

export type User = {
  _id: string
  email: string
  username: string
  role: string
  firstName: string
  lastName: string
  phoneNumber: string
  createdAt: string
  updatedAt: string
}

export type UpdateLoginDetailsResponse = {
  success: boolean
  message: string
  data: {
    user: User
    student: Student
  }
}

const STUDENT_ENDPOINT = {
  STUDENTS: "/students",
}

const getAllStudents = async ({
  page = 1,
  limit = 10,
  searchText,
  class: appliedClass,
  branch,
  session,
  system,
  gender,
  status,
  sortBy,
  section,
  feeGroup,
  _t: cacheBust,
}: StudentFilters = {}): Promise<ApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(STUDENT_ENDPOINT.STUDENTS, {
      page,
      limit,
      searchText,
      class: appliedClass,
      branch,
      session,
      system,
      gender,
      status,
      sortBy,
      section,
      feeGroup,
      ...(typeof cacheBust === "number" && { _t: cacheBust }),
    } as Record<string, string | number | boolean | undefined | null>),
    method: "GET",
  })
  return response.data
}

const updateStudentLoginDetails = async (
  studentId: string,
  loginData: UpdateLoginDetailsRequest,
): Promise<UpdateLoginDetailsResponse> => {
  const response = await apiClient({
    url: `${STUDENT_ENDPOINT.STUDENTS}/login-details/${studentId}`,
    method: "PUT",
    data: loginData,
  })
  return response.data
}

const getStudentById = async (
  studentId: string,
): Promise<{ data: Student }> => {
  const response = await apiClient({
    url: `${STUDENT_ENDPOINT.STUDENTS}/${studentId}`,
    method: "GET",
  })
  return response.data
}

export type StudentImportMismatchSample = {
  gr: string
  name: string
  sheetCampus: string | null
}

export type StudentImportSummary = {
  totalRecords: number
  campusNo: string
  mismatchedCount: number
  mismatchedSamples: StudentImportMismatchSample[]
  alreadyPresentCount: number
  newCount: number
}

export type StudentImportPreviewResponse = StudentImportSummary & {
  success: true
  session: string
}

export type StudentImportStats = {
  created: number
  skipped: number
  skippedWrongCampus: number
  errors: { gr: string; name: string; error: string }[]
  parentsCreated: number
  parentsReused: number
  discountsCreated: number
}

export type StudentImportApplyResponse = {
  success: true
  message: string
  stats: StudentImportStats
}

export class StudentImportError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "StudentImportError"
  }
}

const previewStudentImport = async (file: File): Promise<StudentImportPreviewResponse> => {
  const formData = new FormData()
  formData.append("file", file)
  try {
    const response = await apiClient({
      url: `${STUDENT_ENDPOINT.STUDENTS}/import/preview`,
      method: "POST",
      data: formData,
    })
    return response.data
  } catch (error: any) {
    throw new StudentImportError(error?.response?.data?.message || "Failed to read the uploaded file")
  }
}

const applyStudentImport = async (file: File): Promise<StudentImportApplyResponse> => {
  const formData = new FormData()
  formData.append("file", file)
  try {
    const response = await apiClient({
      url: `${STUDENT_ENDPOINT.STUDENTS}/import/apply`,
      method: "POST",
      data: formData,
    })
    return response.data
  } catch (error: any) {
    throw new StudentImportError(error?.response?.data?.message || "Failed to import students")
  }
}

export {
  getAllStudents,
  updateStudentLoginDetails,
  getStudentById,
  previewStudentImport,
  applyStudentImport,
}
